/**
 * Service: profile display (avatar + background colour).
 *
 * The consumer's PROFILE avatar is one of their OWN generated avatars
 * (`avatar_generations`), chosen by them. Two rules are load-bearing:
 *
 *  1. OWNER SCOPE — reads go through `consumer_profiles` / `avatar_generations`,
 *     both RLS-scoped to `auth.uid()`; the write goes through
 *     `set_profile_display`, which refuses any generation id that is not the
 *     caller's. A forged id can never become someone else's profile picture.
 *  2. DISPLAY NEVER REPROCESSES — the stored `/cutouts/` URL is rendered as-is.
 *     No background removal, no re-generation, no second pass.
 */
import { supabase } from "@/integrations/supabase/client";

export interface ProfileDisplay {
  generationId: string | null;
  /** Stored cutout URL of the chosen generation (resolved for rendering). */
  avatarUrl: string | null;
  backgroundColour: string | null;
}

const EMPTY: ProfileDisplay = { generationId: null, avatarUrl: null, backgroundColour: null };

/** The caller's saved profile display, with the avatar URL resolved. */
export async function getProfileDisplay(userId: string): Promise<ProfileDisplay> {
  const { data, error } = await supabase
    .from("consumer_profiles")
    .select("profile_avatar_generation_id, profile_background_colour")
    .eq("user_id", userId)
    .maybeSingle();
  if (error || !data) return EMPTY;

  const row = data as { profile_avatar_generation_id: string | null; profile_background_colour: string | null };
  let avatarUrl: string | null = null;
  if (row.profile_avatar_generation_id) {
    const { data: gen } = await supabase
      .from("avatar_generations")
      .select("generated_image_url")
      .eq("id", row.profile_avatar_generation_id)
      .maybeSingle();
    avatarUrl = (gen as { generated_image_url: string | null } | null)?.generated_image_url ?? null;
  }
  return {
    generationId: row.profile_avatar_generation_id,
    avatarUrl,
    backgroundColour: row.profile_background_colour,
  };
}

/** Persist the choice. Server-side ownership check lives in the RPC. */
export async function setProfileDisplay(generationId: string | null, backgroundColour: string | null) {
  return supabase.rpc("set_profile_display" as any, {
    p_generation_id: generationId,
    p_background_colour: backgroundColour,
  });
}

export interface OwnAvatar {
  id: string;
  generated_image_url: string | null;
  created_at: string;
}

/**
 * ONE PAGE of the caller's own completed avatars, newest first.
 *
 * The picker must never render the whole history (some accounts have 200+):
 * My Account shows the 5 most recent inline, and the "Other" library pages
 * through the rest on demand. Paging happens in the DATABASE (`range`), not by
 * fetching everything and slicing client-side.
 */
export async function listOwnAvatarsPage(
  userId: string,
  offset: number,
  limit: number,
): Promise<{ rows: OwnAvatar[]; hasMore: boolean }> {
  // Fetch one extra row to learn whether another page exists without a count.
  const { data, error } = await supabase
    .from("avatar_generations")
    .select("id, generated_image_url, created_at")
    .eq("user_id", userId)
    .eq("status", "completed")
    .not("generated_image_url", "is", null)
    .order("created_at", { ascending: false })
    .range(offset, offset + limit);
  if (error || !data) return { rows: [], hasMore: false };
  const rows = data as OwnAvatar[];
  return { rows: rows.slice(0, limit), hasMore: rows.length > limit };
}
